import { GridAlignment } from '@mui/x-data-grid-pro';
import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import {
    abortMessage,
    urlParamReplicaId,
    urlProjectDocuments,
    urlProjectDocumentsData,
} from '../../../Constants';
import { getProjectViewStructure } from '../sliceProjectViewData';
import { IAction, SlicePropsWithData } from '../../store';
import { ensureArray } from '../../../util/DataUtils';
import { apiFetch } from '../../../util/ProjectUtils';

const mapDocumentInfoRequestToDomain = (
    doc: IProjectDocumentsInfoResponse,
): IProjectDocumentInfoItem => {
    const trimmedDocIndex = doc.docIndex?.trim();
    const doctitleSuffix = trimmedDocIndex ? ` - ${trimmedDocIndex}` : '';
    return {
        ...doc,
        title: doc.docNumber ? doc.docNumber + doctitleSuffix : doc.subject,
        universalId: doc.UniversalID,
        Category: ensureArray(doc.Category),
        CategoryID: ensureArray(doc.CategoryID),
        Package: ensureArray(doc.Package),
        PackageID: ensureArray(doc.PackageID),
        KeywordID: ensureArray(doc.KeywordID),
        AddressID: ensureArray(doc.AddressID),
    };
};

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsState = {
    data: [],
    stash: [],
    loading: false,
    runPagination: false,
    error: null,
    info: {
        data: [],
        loading: false,
        error: null,
    },
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    /**
     * Replace the documents with new ones.
     */
    setDocuments(state: IProjectDocumentsState, action: IAction<IDocDefaultFields[]>) {
        state.data = action.payload;
    },
    /**
     * Store the documents in the stash.
     */
    stashDocuments(state: IProjectDocumentsState) {
        state.stash = state.data;
    },
    /**
     * Restore the documents from the stash (and flush it).
     */
    revertDocuments(state: IProjectDocumentsState) {
        state.data = state.stash;
        state.stash = [];
    },
    /**
     * Clears the documents collection.
     */
    clearDocuments(state: IProjectDocumentsState) {
        state.data = [];
        state.runPagination = false;
    },

    removeProjectDocumentsInfoItem(
        state: IProjectDocumentsState,
        action: IAction<{ universalId: string }>,
    ) {
        state.info.data = state.info.data.filter(
            doc => doc.universalId !== action.payload.universalId,
        );
    },
    clearProjectDocumentsInfo(state: IProjectDocumentsState) {
        state.info.data = [];
    },
    paginationFinished(state: IProjectDocumentsState) {
        state.runPagination = false;
    },
};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the documents contained within a project database view category.
     * @param config - Object that contains the project database's replicaId, the active view, and the category to load documents for.
     * @returns Promise for the HTTP request.
     */
    getProjectDocuments: async (config: {
        replicaId: string;
        viewName: string;
        category: string;
        fields: string[];
        offset: number;
        limit: number;
    }): Promise<IDocDefaultFields[]> => {
        try {
            const { replicaId, viewName, category, fields } = config;

            const request: RequestInit = {
                headers: {
                    /** URL encode category, otherwise special characters (äöüß) will block the request being sent.  */
                    category: encodeURIComponent(category ?? ''),
                    fieldset: fields.join(';'),
                    viewName,
                    offset: config.offset.toString(),
                    limit: config.limit.toString(),
                },
            };

            const url = urlProjectDocuments.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },
    /**
     * Get documents for Upload Additional Documents Dialog
     */
    getProjectDocumentsInfo: async (config: { documentIds: string[]; replicaId: string }) => {
        const { replicaId, documentIds } = config;
        try {
            // 7114: ensure fields below for categorization functionality
            const fields = [
                'docIndex',
                'docNumber',
                'subject',
                'CategoryID',
                'PackageID',
                'KeywordID',
                'AddressID',
            ];

            const formData = new FormData();
            formData.append('universalIds', documentIds.join('|'));
            formData.append('fieldset', fields.join(';'));

            const request: RequestInit = {
                method: 'POST',
                body: formData,
            };

            const url = urlProjectDocumentsData.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsState>) => {
        /**
         * Reducers for the 'getProjectDocuments' thunk.
         */

        builder.addCase(getProjectDocuments.pending, (state: Draft<IProjectDocumentsState>) => {
            state.loading = !state.runPagination;
            state.runPagination = true;
            state.error = null;
        });
        builder.addCase(
            getProjectDocuments.fulfilled,
            (state: Draft<IProjectDocumentsState>, action: IAction<IDocDefaultFields[]>) => {
                const mappedIds = state.data.map(doc => doc.UniversalID);

                state.loading = false;
                state.error = null;
                state.data = state.runPagination
                    ? [
                          ...state.data,
                          ...action.payload.filter(doc => !mappedIds.includes(doc.UniversalID)),
                      ]
                    : action.payload;
            },
        );
        builder.addCase(
            getProjectDocuments.rejected,
            (state: Draft<IProjectDocumentsState>, action: IAction<SerializedError>) => {
                // Ignore internal abort "errors"
                if (action.error.message === abortMessage) {
                    state.runPagination = false;
                    return;
                }

                state.loading = false;
                state.runPagination = false;
                state.error = action.error;
                state.data = [];
            },
        );

        /**
         * Reducers for the 'getProjectViewStructure' thunk from sliceProjectData.
         */
        builder.addCase(
            getProjectViewStructure.fulfilled,
            (state: Draft<IProjectDocumentsState>) => {
                state.data = [];
            },
        );

        /**
         * Reducers for the 'getProjectDocumentsInfo' thunk.
         */
        builder.addCase(getProjectDocumentsInfo.pending, (state: Draft<IProjectDocumentsState>) => {
            state.info.loading = true;
            state.info.error = null;
        });

        builder.addCase(
            getProjectDocumentsInfo.fulfilled,
            (
                state: Draft<IProjectDocumentsState>,
                action: IAction<IProjectDocumentsInfoResponse[]>,
            ) => {
                state.info.data = action.payload.map(mapDocumentInfoRequestToDomain);

                state.info.loading = false;
                state.info.error = null;
            },
        );

        builder.addCase(
            getProjectDocumentsInfo.rejected,
            (state: Draft<IProjectDocumentsState>, action: IAction<SerializedError>) => {
                state.info.loading = false;
                state.info.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocuments = createAsyncThunk(
    'project/documents',
    thunks.getProjectDocuments,
);

export const getProjectDocumentsInfo = createAsyncThunk(
    'project/documents/info',
    thunks.getProjectDocumentsInfo,
);

export interface IProjectDocumentsInfoResponse {
    DocType: string;
    Files: IDocFileInfo;
    IdDocIndex: string;
    NoteId: string;
    UniversalID: string;
    docNumber: string;
    subject: string;
    docIndex: string;
    Category?: string | string[];
    CategoryID?: string | string[];
    Package?: string | string[];
    PackageID?: string | string[];
    KeywordID?: string | string[];
    AddressID?: string | string[];
}

export interface IProjectDocumentInfoItem extends IProjectDocumentsInfoResponse {
    title: string;
    universalId: string;
    Category: string[];
    CategoryID: string[];
    Package: string[];
    PackageID: string[];
    KeywordID: string[];
    AddressID: string[];
}

export interface IProjectDocumentsInfoState {
    info: SlicePropsWithData<IProjectDocumentInfoItem[]>;
    stash: IDocDefaultFields[];
    runPagination: boolean;
}

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocumentsState = SlicePropsWithData<IDocDefaultFields[]> &
    IProjectDocumentsInfoState;

/**
 * Interface that describes the default fields that are always requested for documents.
 */
export interface IDocDefaultFields {
    /** Document unique identifier. */
    NoteId: string;

    /** Document Id for all indexes of the document. */
    IdDocIndex: string;

    /** Unique Id  of the document. */
    UniversalID: string;

    /** Document's DocType. */
    DocType: string;

    /** The number of additional documents associated with this document. */
    ChildCount?: number;

    /** The number of redlining documents associated with this document. */
    RedliningCount?: number;

    /** Details on the document's file attachments. */
    Files: IDocFileInfo;

    /** Subject of the document. */
    Subject: string;

    /** Shows if it's a test document. */
    TestMode: boolean;

    /** Icon depends on document's release status. */
    ViewIcon_DocumentRelease: ViewIconDocumentReleaseStatus;

    /** Document's workflow id. */
    wfId?: string;

    /** Document's DocNumber. */
    DocNumber?: string;

    /** Document's DocIndex. */
    DocIndex?: string;

    /** Document's DocVariant. */
    DocVariant?: string;

    /** Document's sign status. */
    ewfSignStatus?: string;

    /** Document's JobBackend field (Automated Job). */
    JobBackend?: string;

    /** Document's DocIndexNext. */
    DocIndexNext?: string;

    /** Pages on which Redlinings were made (comma separated numbers). */
    RedliningPage?: string;

    /** Document's file info. */
    File?: string;
}

export type ViewIconDocumentReleaseStatus =
    | 'View_DocStatusEdit_NotReleased'
    | 'View_DocStatusEdit_Released';

/**
 * Interface that describes the contents of the files attached to a document.
 */
export interface IDocFileInfo {
    /** Name of the document's main file attachment. */
    FileName: string;

    /** The total number of files attached to the document (including the main attachment). */
    Count: number;

    /** Wether multiple files are attached to the document, specifically in the "File" section. */
    MultipleFiles?: boolean;
}

/**
 * Interface that describes the configuration options for a document field.
 */
export interface IFieldConfig {
    /** Field name/id. */
    field: string;

    /** Translations for the column's header. */
    headerName: { [id: string]: string };

    /** Field's data type */
    type: FieldType;

    /** List of database fields that make up this field's value */
    fields: string[];

    /** Property name, or template string. */
    value: string;

    /** Optional number that sets the initial width of the field. */
    width?: number;

    /** Optional alignment for the field's contents. */
    align?: GridAlignment;

    /** Optional boolean to disable the column's context menu. */
    disableColumnMenu?: boolean;

    /** Optional boolean to prevent the column's position from being changed. */
    disableReorder?: boolean;

    /** Whether the field's column can be resized. */
    resizable: boolean;

    /** Wether the field's column can be edited. */
    editable?: boolean;

    /** Whether the field's column can be filtered. */
    filterable: boolean;

    /** Whether the field's column can be sorted. */
    sortable: boolean;

    /** Whether the field's column can be hided. */
    hideable: boolean;

    /** Whether the field's column visible. */
    visible: boolean;

    /** Optional boolean that defines the field with hover buttons. */
    hoverBtns?: boolean;

    /** Whether the Grid should default sort by this field. */
    sorted?: boolean;
}

/**
 * Create the redux slice.
 */
export const projectDocumentsSlice = createSlice({
    name: 'projectDocuments',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Types of fields supported in the application.
 */
export type FieldType =
    | 'string'
    | 'icon'
    | 'number'
    | 'numberRelative'
    | 'user'
    | 'date'
    | 'dateRelative'
    | 'status'
    | 'wfHistory'
    | 'files'
    | 'redlinings'
    | 'categoryAutomatic'
    | 'multiline'
    | ImportFieldType;

/**
 * Types of fields specific for import area.
 */
type ImportFieldType = 'importCategory' | 'importPackage' | 'creationDate' | 'editableString';

/**
 * Export functions from slice actions.
 */
export const {
    setDocuments,
    stashDocuments,
    revertDocuments,
    clearDocuments,
    removeProjectDocumentsInfoItem,
    clearProjectDocumentsInfo,
    paginationFinished,
} = projectDocumentsSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsSlice.reducer;
